import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Flag, Lightbulb, Rocket, Wrench } from 'lucide-react';
import { LAUNCH_SERVICES, LAUNCH_STARTING_POINTS, LAUNCH_STARTING_POINT_KEY } from '@/data/launch';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const ACCENT = '#4225C4';
const ACCENT_DARK = '#331BA0';
const ACCENT_SOFT = '#E4E0FA';

const OPTION_ICONS = { lightbulb: Lightbulb, flag: Flag, wrench: Wrench, rocket: Rocket };

export const StartingPoint = () => {
  const [selectedId, setSelectedId] = useState(
    () => window.sessionStorage.getItem(LAUNCH_STARTING_POINT_KEY) || null
  );
  const reduce = useReducedMotion();
  const selected = LAUNCH_STARTING_POINTS.options.find((o) => o.id === selectedId);

  const select = (id) => {
    setSelectedId(id);
    window.sessionStorage.setItem(LAUNCH_STARTING_POINT_KEY, id);
  };

  const serviceName = (slug) => LAUNCH_SERVICES.find((s) => s.slug === slug)?.name;
  const serviceHref = (slug) => LAUNCH_SERVICES.find((s) => s.slug === slug)?.href;

  return (
    <section aria-labelledby="starting-point-heading" data-testid="starting-point-section" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center" data-testid="starting-point-header">
          <p className="flex items-center justify-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest" style={{ color: ACCENT }}>01</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
              {LAUNCH_STARTING_POINTS.eyebrow}
            </span>
          </p>
          <h2 id="starting-point-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            {LAUNCH_STARTING_POINTS.heading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{LAUNCH_STARTING_POINTS.intro}</p>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label="Choose your starting point">
          {LAUNCH_STARTING_POINTS.options.map((option, i) => {
            const Icon = OPTION_ICONS[option.icon];
            const isSelected = option.id === selectedId;
            return (
              <Reveal key={option.id} delay={i * 0.06} className="h-full">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => select(option.id)}
                  data-testid={`starting-point-${option.id}`}
                  className={cn(
                    'relative flex h-full w-full flex-col rounded-xl border-2 bg-white p-6 text-left transition-[border-color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                    isSelected
                      ? 'border-navy-900 bg-navy-50 shadow-card'
                      : 'border-slate-200 hover:border-navy-300 hover:shadow-sm'
                  )}
                >
                  <span
                    className={cn(
                      'absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors',
                      isSelected ? 'border-navy-900 bg-navy-900 text-white' : 'border-slate-300 text-transparent'
                    )}
                    aria-hidden="true"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-lg transition-colors"
                    style={{
                      backgroundColor: isSelected ? ACCENT : ACCENT_SOFT,
                      color: isSelected ? '#ffffff' : ACCENT,
                    }}
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <span className="mt-4 block text-base font-bold leading-snug text-navy-900">{option.title}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-slate-500">{option.description}</span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <div aria-live="polite">
          <AnimatePresence mode="wait">
            {selected && (
              <motion.div
                key={selected.id}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid="starting-point-recommendation"
                className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 shadow-card sm:p-9"
              >
                <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center md:gap-10">
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: ACCENT_DARK }}>
                      <Check className="h-4 w-4" aria-hidden="true" />
                      Recommended next step
                    </p>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-navy-800 sm:text-base">
                      {selected.recommendation}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2.5">
                      {selected.relevantServices.map((slug) => (
                        <Link
                          key={slug}
                          to={serviceHref(slug)}
                          data-testid={`starting-point-service-${slug}`}
                          className="rounded-full border px-4 py-1.5 text-xs font-bold transition-colors hover:bg-navy-50"
                          style={{ borderColor: `${ACCENT}55`, color: ACCENT_DARK }}
                        >
                          {serviceName(slug)}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <Link
                    to={`/?service=launch&source=Launch%20Page&startingPoint=${selected.id}#solution-finder`}
                    data-testid="starting-point-discuss-btn"
                    className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                  >
                    Discuss This With Amden
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
